/**
 * Nawkout Meta CAPI Client-Side Collector (Storefront)
 *
 * Sends enriched events to our server for Meta CAPI forwarding.
 * Replaces Analyzify ($109/mo) for storefront events.
 *
 * Events sent from here (storefront pages only):
 *   PageView     — every page load
 *   ViewContent  — product page / LP view
 *
 * Events sent from Custom Pixel (Shopify sandbox — more reliable):
 *   AddToCart         — product_added_to_cart (via analytics.subscribe)
 *   InitiateCheckout  — checkout_started
 *   AddPaymentInfo    — payment_info_submitted
 *
 * Purchase is handled by the Shopify order webhook (most data-rich).
 *
 * EMQ enrichment (Mar 2026):
 *   - nk_eid: persistent first-party UUID for all visitors → external_id
 *   - nk_fbclid: localStorage persistence for Safari ITP resilience → fbc
 *
 * Shopify data sharing should be set to "Standard" (pixel only) so our
 * server-side events are the sole CAPI source — no dedup conflict.
 */
(function() {
  'use strict';

  // Collection endpoint on our FastAPI backend
  var COLLECT_URL = 'https://crm.dblitz.com/api/capi/collect';

  // ── Cookie helpers ───────────────────────────────────────────────
  function getCookie(name) {
    var cookies = document.cookie.split(';');
    for (var i = 0; i < cookies.length; i++) {
      var c = cookies[i].trim();
      if (c.indexOf(name + '=') === 0) return c.substring(name.length + 1);
    }
    return '';
  }

  function setCookie(name, value, days) {
    var d = new Date();
    d.setTime(d.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = name + '=' + value + '; expires=' + d.toUTCString() + '; path=/; SameSite=Lax';
  }

  // ── Persistent external ID (nk_eid) ─────────────────────────────
  // UUID cookie for ALL visitors — logged-in or anonymous.
  // Meta uses external_id for cross-device matching (~16% EMQ boost).
  // Dual-stored in cookie + localStorage for maximum persistence.
  function getOrCreateExternalId() {
    var eid = getCookie('nk_eid');
    if (!eid) {
      try { eid = localStorage.getItem('nk_eid') || ''; } catch(e) {}
    }
    if (!eid) {
      eid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        var r = Math.random() * 16 | 0;
        return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
      });
    }
    setCookie('nk_eid', eid, 730);
    try { localStorage.setItem('nk_eid', eid); } catch(e) {}
    return eid;
  }

  // ── fbclid persistence (Safari ITP fix) ──────────────────────────
  // Safari ITP kills _fbc cookies from URL-decorated links within 24h.
  // localStorage is NOT subject to ITP restrictions.
  // Store fbclid when user arrives from Meta ad, use it for fbc on later visits.
  function persistFbclid() {
    var params = new URLSearchParams(window.location.search);
    var fbclid = params.get('fbclid');
    if (fbclid) {
      try {
        localStorage.setItem('nk_fbclid', fbclid);
        localStorage.setItem('nk_fbclid_ts', String(Date.now()));
      } catch(e) {}
    }
  }

  // ── Get fbc with localStorage fallback ───────────────────────────
  function getFbc() {
    // 1. Meta's native _fbc cookie (best — set by pixel)
    var fbc = getCookie('_fbc');
    if (fbc) return fbc;

    // 2. fbclid in current URL (ad click landing)
    var params = new URLSearchParams(window.location.search);
    var fbclid = params.get('fbclid');
    if (fbclid) return 'fb.1.' + Date.now() + '.' + fbclid;

    // 3. localStorage fallback (survives Safari ITP — 7d+ persistence)
    try {
      var stored = localStorage.getItem('nk_fbclid');
      if (stored) {
        var ts = localStorage.getItem('nk_fbclid_ts') || String(Date.now());
        return 'fb.1.' + ts + '.' + stored;
      }
    } catch(e) {}

    return '';
  }

  // ── Generate unique event ID ───────────────────────────────────
  function eventId(prefix) {
    return prefix + '_' + Date.now() + '_' + Math.random().toString(36).substr(2, 8);
  }

  // ── Build user_data from available sources ─────────────────────────
  function getUserData() {
    var ud = {
      client_user_agent: navigator.userAgent
    };

    var fbp = getCookie('_fbp');
    // Synthetic fbp if Meta pixel hasn't set one yet (Littledata approach)
    if (!fbp) {
      fbp = 'fb.1.' + Date.now() + '.' + Math.floor(Math.random() * 10000000000);
    }
    ud.fbp = fbp;

    var fbc = getFbc();
    if (fbc) ud.fbc = fbc;

    // external_id: Shopify customer ID for logged-in, nk_eid UUID for anonymous
    if (window.__st && window.__st.cid) {
      ud.external_id = String(window.__st.cid);
    } else {
      ud.external_id = getOrCreateExternalId();
    }

    return ud;
  }

  // ── Send event to collection endpoint ──────────────────────────
  function sendEvent(eventName, eid, customData) {
    var payload = {
      event_name: eventName,
      event_id: eid,
      event_source_url: window.location.href,
      user_data: getUserData(),
      custom_data: customData || {}
    };

    var json = JSON.stringify(payload);

    // Use sendBeacon with Blob for proper Content-Type + page-navigation reliability
    if (navigator.sendBeacon) {
      var blob = new Blob([json], { type: 'application/json' });
      navigator.sendBeacon(COLLECT_URL, blob);
    } else {
      fetch(COLLECT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: json,
        keepalive: true
      }).catch(function() {});
    }
  }

  // ── PageView — fires on every page ─────────────────────────────
  function trackPageView() {
    sendEvent('PageView', eventId('nk_pv'), {
      content_name: document.title || window.location.pathname
    });
  }

  // ── ViewContent — fires on product pages and landing pages ─────
  function trackViewContent() {
    // Product pages
    if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta) {
      var meta = window.ShopifyAnalytics.meta;
      if (meta.product) {
        var currency = 'USD';
        if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
          currency = window.Shopify.currency.active;
        }
        sendEvent('ViewContent', eventId('nk_vc'), {
          content_ids: [String(meta.product.id)],
          content_type: 'product',
          content_name: meta.product.title || '',
          value: meta.product.price ? meta.product.price / 100 : 0,
          currency: currency
        });
        return;
      }
    }

    // Landing pages (LP or creator pages)
    var path = window.location.pathname;
    if (path.indexOf('/pages/lp-') === 0 || path.indexOf('/pages/creator-') === 0) {
      sendEvent('ViewContent', eventId('nk_vc'), {
        content_type: 'product',
        content_name: document.title || path,
        content_category: 'landing_page'
      });
    }
  }

  // ── Initialize ─────────────────────────────────────────────────
  function init() {
    persistFbclid();       // Store fbclid from URL before ITP kills it
    getOrCreateExternalId(); // Ensure nk_eid exists on first page load
    trackPageView();
    trackViewContent();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
